/**
 * React NodeView for `dateChip` — Phase 4b inline atomic chip.
 *
 * 2026-05-29 redesign: drop the `(pick a date)` italic placeholder
 * and the native `<input type="date">` popover. The chip now opens
 * a real month-grid calendar (`<CalendarPicker>`) directly on click.
 * Empty state renders just the calendar icon (no placeholder text)
 * and the picker auto-opens on insert so the user lands in the
 * calendar immediately — `/date` flow is one click + one date pick.
 *
 * Locale-aware re-render: `useTranslation()` re-renders the NodeView
 * when `i18n.language` changes. Date formatting goes through
 * `formatNamed(i18n, ..., 'mediumDate')` — never raw `Intl.*` per
 * the `[FE/i18n]` Intl-singleton pitfall.
 */

import { NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react'
import { Calendar } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CalendarPicker } from './CalendarPicker'
import { isIsoDate } from './date-chip-extension'

export function DateChipView({ editor, node, updateAttributes }: ReactNodeViewProps<HTMLElement>) {
  const { t } = useTranslation('wiki')
  const rawDate = node.attrs.date as string | null
  const date: string | null = isIsoDate(rawDate) ? rawDate : null

  const [popoverOpen, setPopoverOpen] = useState(false)
  // Anchor ref the calendar reads to compute its viewport-fixed
  // position. The picker portals to `document.body`, so without this
  // anchor it has no reference for placement.
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  // Read-only when the editor isn't in edit mode — view-mode clicks on
  // the chip should NOT open the calendar (the doc is being read, not
  // authored). The bool tracks `editor.isEditable` reactively so a
  // view→edit transition immediately re-enables the popover.
  const isEditable = editor.isEditable

  // The `/date` slash command pre-populates the chip with today's
  // date (2026-05-29), so the auto-open-on-mount path is gone. The
  // calendar still opens on click — same code path as a user clicking
  // to CHANGE the date. Legacy chips with `date: null` (none today
  // but defensive for round-trip) render the icon only and rely on
  // click to open.

  const ariaLabel = t('date.ariaLabel', { defaultValue: 'Date chip' })
  const editAriaLabel = t('date.editAriaLabel', { defaultValue: 'Pick a date' })

  // Display label: locked to American "Month Day, Year" format
  // (e.g. "May 29, 2026") regardless of UI locale — user directive
  // 2026-05-29. Pinned to `en-US` rather than routed through the i18n
  // `mediumDate` formatter because that formatter is locale-aware
  // (would produce "29 may 2026" in Spanish, etc.). The chip's
  // display stays American everywhere.
  //
  // `new Date('YYYY-MM-DD')` defaults to UTC midnight, which can
  // render one day off in negative offsets — appending `T00:00` parses
  // in the local time zone (what the user picked).
  const display = useMemo(() => {
    if (!date) return null
    const dt = new Date(`${date}T00:00`)
    if (Number.isNaN(dt.getTime())) return null
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(dt)
  }, [date])

  const onPillMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (!isEditable) return
      setPopoverOpen((o) => !o)
    },
    [isEditable],
  )

  const onChange = useCallback(
    (next: string) => {
      updateAttributes({ date: next })
    },
    [updateAttributes],
  )

  return (
    <NodeViewWrapper
      as="span"
      data-block-type="date-chip"
      data-date={date ?? ''}
      className="relative inline-block align-baseline"
    >
      <button
        ref={buttonRef}
        type="button"
        onMouseDown={onPillMouseDown}
        aria-label={date === null ? editAriaLabel : `${ariaLabel}: ${display}`}
        title={editAriaLabel}
        className="wiki-inline-chip wiki-inline-chip--date text-fg"
      >
        <Calendar
          size={12}
          aria-hidden="true"
          className={display === null ? 'inline' : 'me-1 inline'}
        />
        {display}
      </button>
      {popoverOpen ? (
        <CalendarPicker
          date={date}
          onChange={onChange}
          onClose={() => setPopoverOpen(false)}
          anchorRef={buttonRef}
        />
      ) : null}
    </NodeViewWrapper>
  )
}

DateChipView.displayName = 'DateChipView'
